import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { 
  Heart, 
  Crown, 
  Star, 
  Check, 
  Sparkles,
  Lock,
  Gift,
  MessageCircle,
  Video,
  Music,
  Loader2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

/**
 * SubscribeButton - Button to open subscription modal for a creator
 * 
 * Props:
 * - artistId: string - The artist's ID
 * - artistName: string - The artist's display name
 * - size: 'sm' | 'default' | 'lg' - Button size
 * - className: string - Additional classes
 */
export function SubscribeButton({ artistId, artistName, size = 'default', className }) {
  const { isAuthenticated, user, getAuthHeaders } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);

  // Default tiers if none loaded
  const defaultTiers = [
    {
      tier_id: 'free',
      name: 'Free Follower',
      price_cents: 0,
      description: 'Follow the artist and stay updated',
      benefits: [
        'See public posts and content',
        'Get notified of new releases',
        'Like and comment on public posts'
      ],
      icon: Heart,
      color: 'gray'
    },
    {
      tier_id: 'supporter',
      name: 'Supporter',
      price_cents: 499,
      description: 'Support the artist and unlock exclusive content',
      benefits: [
        'All Free Follower benefits',
        'Subscriber badge on profile',
        'Exclusive subscriber-only posts',
        'Early access to new music/videos',
        'Subscriber-only comments'
      ],
      icon: Star,
      color: 'secondary'
    },
    {
      tier_id: 'vip',
      name: 'VIP',
      price_cents: 999,
      description: 'Ultimate fan experience with premium access',
      benefits: [
        'All Supporter benefits',
        'VIP-only live streams',
        'Behind-the-scenes content',
        'Priority replies from artist',
        'Exclusive merch discounts',
        'Name in credits'
      ],
      icon: Crown,
      color: 'amber'
    }
  ];

  useEffect(() => {
    if (isOpen && artistId) {
      fetchTiers();
      if (isAuthenticated) {
        checkSubscriptionStatus();
      }
    }
  }, [isOpen, artistId, isAuthenticated]);

  const fetchTiers = async () => {
    try {
      const res = await fetch(`${API}/fan-subscriptions/tiers/${artistId}`);
      if (res.ok) {
        const result = await safeJson(res);
        if (result.data?.tiers && result.data.tiers.length > 0) {
          setTiers(result.data.tiers);
        } else {
          setTiers(defaultTiers);
        }
      } else {
        setTiers(defaultTiers);
      }
    } catch (error) {
      console.error('Error fetching tiers:', error);
      setTiers(defaultTiers);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      const res = await fetch(`${API}/fan-subscriptions/check/${artistId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const result = await safeJson(res);
        // API returns {is_subscribed, subscription} directly, not wrapped in data
        setSubscriptionStatus(result.data || result);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleSubscribe = async (tierId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to subscribe');
      return;
    }

    setLoading(true);
    setSelectedTier(tierId);

    try {
      // Find the tier to check if it's paid
      const tier = displayTiers.find(t => t.tier_id === tierId);
      const isPaid = tier && tier.price_cents > 0;

      if (isPaid) {
        // Use real Stripe subscription endpoint for paid tiers
        const res = await fetch(`${API}/fan-subscriptions/subscribe/${artistId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ tier_id: tierId })
        });

        const result = await safeJson(res);

        if (res.ok && result.data?.checkout_url) {
          toast.info('Redirecting to payment...', { duration: 2000 });
          window.location.href = result.data.checkout_url;
          return;
        } else {
          toast.error(result.data?.detail || 'Failed to start subscription');
        }
      } else {
        // Free tier — use mock subscribe
        const res = await fetch(`${API}/fan-subscriptions/mock-subscribe/${artistId}?tier_id=${tierId}`, {
          method: 'POST',
          headers: getAuthHeaders()
        });

        const result = await safeJson(res);

        if (res.ok && result.data?.success) {
          toast.success(result.data.message);
          setSubscriptionStatus({ is_subscribed: true, subscription: result.data.subscription });
          setIsOpen(false);
        } else {
          toast.error(result.data?.detail || 'Failed to subscribe');
        }
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Failed to subscribe');
    } finally {
      setLoading(false);
      setSelectedTier(null);
    }
  };

  const currentTier = subscriptionStatus?.subscription?.tier_id;
  const isSubscribed = subscriptionStatus?.is_subscribed;

  // Determine button text and style
  const getButtonContent = () => {
    if (isSubscribed) {
      const tierName = subscriptionStatus?.subscription?.tier_name || 'Subscribed';
      return (
        <>
          <Check className="w-4 h-4 mr-2" />
          {tierName}
        </>
      );
    }
    return (
      <>
        <Heart className="w-4 h-4 mr-2" />
        Subscribe
      </>
    );
  };

  const displayTiers = tiers.length > 0 ? tiers : defaultTiers;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant={isSubscribed ? "outline" : "default"}
          size={size}
          className={cn(
            isSubscribed && "border-green-500/30 text-green-500",
            className
          )}
          data-testid="subscribe-button"
        >
          {getButtonContent()}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-secondary" />
            Support {artistName}
          </DialogTitle>
          <DialogDescription>
            Choose how you want to support this creator. Cancel anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {displayTiers.map((tier, index) => {
            const TierIcon = tier.icon || (index === 0 ? Heart : index === 1 ? Star : Crown);
            const isCurrentTier = currentTier === tier.tier_id;
            const isPaidTier = tier.price_cents > 0;
            const tierColor = tier.color || (index === 0 ? 'gray' : index === 1 ? 'secondary' : 'amber');

            return (
              <Card 
                key={tier.tier_id}
                className={cn(
                  "p-4 cursor-pointer transition-all border-2",
                  isCurrentTier 
                    ? "border-green-500 bg-green-500/5" 
                    : "border-transparent hover:border-border",
                  selectedTier === tier.tier_id && "opacity-50"
                )}
                onClick={() => !isCurrentTier && !loading && handleSubscribe(tier.tier_id)}
                data-testid={`tier-card-${tier.tier_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "p-2 rounded-full",
                      tierColor === 'secondary' ? "bg-secondary/20" :
                      tierColor === 'amber' ? "bg-amber-500/20" :
                      "bg-muted"
                    )}>
                      <TierIcon className={cn(
                        "w-5 h-5",
                        tierColor === 'secondary' ? "text-secondary" :
                        tierColor === 'amber' ? "text-amber-500" :
                        "text-muted-foreground"
                      )} />
                    </div>
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        {tier.name}
                        {isCurrentTier && (
                          <Badge variant="outline" className="text-green-500 border-green-500/30">
                            Current
                          </Badge>
                        )}
                      </h3>
                      <p className="text-sm text-muted-foreground">{tier.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {tier.price_cents === 0 ? (
                      <span className="text-lg font-bold text-green-500">Free</span>
                    ) : (
                      <>
                        <span className="text-lg font-bold">${(tier.price_cents / 100).toFixed(2)}</span>
                        <span className="text-sm text-muted-foreground">/month</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Benefits list */}
                <ul className="space-y-2">
                  {(tier.benefits || []).map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                {/* Pricing detail for paid tiers */}
                {isPaidTier && !isCurrentTier && (
                  <div className="mt-4 flex items-center justify-end">
                    <span className="text-xs text-muted-foreground">
                      85% goes to creator
                    </span>
                  </div>
                )}

                {/* Subscribe button for current card */}
                {!isCurrentTier && (
                  <Button 
                    className="w-full mt-4" 
                    variant={isPaidTier ? "outline" : "default"}
                    disabled={loading}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSubscribe(tier.tier_id);
                    }}
                    data-testid={`subscribe-tier-${tier.tier_id}`}
                  >
                    {loading && selectedTier === tier.tier_id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : isPaidTier ? (
                      <Crown className="w-4 h-4 mr-2" />
                    ) : (
                      <Heart className="w-4 h-4 mr-2" />
                    )}
                    {isPaidTier ? `Subscribe $${(tier.price_cents / 100).toFixed(2)}/mo` : 'Follow Free'}
                  </Button>
                )}
              </Card>
            );
          })}
        </div>

        {/* Footer info */}
        <div className="text-center text-xs text-muted-foreground pt-2 border-t border-border">
          <p>Support creators you love. Cancel anytime.</p>
          <p className="mt-1">Earnings are held until payout setup is complete.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * SubscriptionBadge - Shows subscription tier badge on user avatar/card
 */
export function SubscriptionBadge({ subscription, size = 'sm' }) {
  if (!subscription || subscription.status !== 'active') return null;

  const tierConfig = {
    free: { icon: Heart, color: 'gray', label: 'Follower' },
    supporter: { icon: Star, color: 'secondary', label: 'Supporter' },
    vip: { icon: Crown, color: 'amber', label: 'VIP' }
  };

  const config = tierConfig[subscription.tier_id] || tierConfig.supporter;
  const Icon = config.icon;

  return (
    <Badge 
      className={cn(
        "gap-1",
        config.color === 'secondary' ? "bg-secondary/20 text-secondary" :
        config.color === 'amber' ? "bg-amber-500/20 text-amber-500" :
        "bg-muted text-muted-foreground",
        size === 'sm' ? "text-xs px-1.5 py-0.5" : "text-sm px-2 py-1"
      )}
    >
      <Icon className={size === 'sm' ? "w-3 h-3" : "w-4 h-4"} />
      {config.label}
    </Badge>
  );
}

/**
 * ContentGate - Wraps content that requires subscription
 */
export function ContentGate({ 
  requiredTier = 'supporter', // 'free', 'supporter', 'vip'
  userTier = null,
  artistId,
  artistName,
  children,
  showPreview = true
}) {
  const tierLevels = { free: 0, supporter: 1, vip: 2 };
  const requiredLevel = tierLevels[requiredTier] || 0;
  const userLevel = tierLevels[userTier] || 0;

  const hasAccess = userLevel >= requiredLevel;

  if (hasAccess) {
    return children;
  }

  // Show locked content preview
  return (
    <div className="relative">
      {showPreview && (
        <div className="opacity-30 blur-sm pointer-events-none">
          {children}
        </div>
      )}
      <div className={cn(
        "absolute inset-0 flex flex-col items-center justify-center bg-background/80",
        !showPreview && "relative min-h-[200px] rounded-lg"
      )}>
        <Lock className="w-8 h-8 text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground mb-3">
          {requiredTier === 'vip' ? 'VIP-only content' : 'Subscriber-only content'}
        </p>
        <SubscribeButton 
          artistId={artistId} 
          artistName={artistName}
          size="sm"
        />
      </div>
    </div>
  );
}

export default SubscribeButton;
